import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { api } from "../api/client";
import { getRedirectUri } from "../lib/cognito";
import { signInWithCognito } from "../lib/auth";

type ExchangeResponse = {
  id_token: string;
  expires_in: number | null;
  claims: Record<string, unknown>;
};

// Verifies the actual Cognito signing flow end-to-end: this page only
// renders once the backend has independently verified the ID token's
// signature (see routes_auth_cognito.py) — the claims shown below are
// the real, signature-checked contents of a real Cognito-issued token.
export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"exchanging" | "done" | "error">("exchanging");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExchangeResponse | null>(null);
  const submittedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const cognitoError = params.get("error");
    if (cognitoError) {
      setStatus("error");
      setError(params.get("error_description") || cognitoError);
      return;
    }

    const code = params.get("code");
    if (!code) {
      setStatus("error");
      setError("No authorization code in the callback URL.");
      return;
    }

    // Authorization codes are single-use — StrictMode's dev-mode double
    // effect invocation would otherwise burn the code on a duplicate call.
    if (submittedCodeRef.current === code) return;
    submittedCodeRef.current = code;

    api
      .post<ExchangeResponse>("/api/v1/auth/cognito/exchange", { code, redirect_uri: getRedirectUri() })
      .then((r) => {
        setResult(r);
        setStatus("done");
        signInWithCognito(r.id_token);
        navigate("/projects", { replace: true });
      })
      .catch((e: Error) => {
        setError(e.message);
        setStatus("error");
      });
  }, [params, navigate]);

  return (
    <Box className="login-page">
      <Card className="login-card login-card-wide">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cognito sign-in
          </Typography>

          {status === "exchanging" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Exchanging authorization code with Cognito…</Typography>
            </Box>
          )}

          {status === "error" && (
            <>
              <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                {error}
              </Alert>
              <Link to="/login">← Back to sign in</Link>
            </>
          )}

          {status === "done" && result && (
            <>
              <Alert severity="success" sx={{ mt: 1, mb: 2 }}>
                Signature verified — this is a real, Cognito-issued ID token.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>sub:</b> {String(result.claims.sub)}
                <br />
                <b>email:</b> {String(result.claims.email ?? "—")}
                <br />
                <b>custom:org_id:</b> {String(result.claims["custom:org_id"] ?? "— not present in token —")}
                <br />
                <b>expires in:</b> {result.expires_in ?? "—"}s
              </Typography>
              <Box
                component="pre"
                sx={{
                  fontSize: 12,
                  background: "var(--code-bg, #f0f0f0)",
                  p: 2,
                  borderRadius: 1,
                  overflowX: "auto",
                }}
              >
                {JSON.stringify(result.claims, null, 2)}
              </Box>
              <Link to="/login">← Back to sign in</Link>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
